<template>
    <div
        id="widgetContainer"
        ref="widgetContainer"
        class="zc-widget-config"
        :data-restaurant="restaurantId"
        data-open="false"
        :data-primary-color="primaryColor"
    />
</template>

<script setup>
const props = defineProps({
    restaurantId: {
        type: String,
        required: true
    },
    themeColor: {
        type: String,
        required: false,
        default: 'hsl(125, 8%, 30%)'
    }
});

function hslToHex(h, s, l) {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = n => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');   // convert to Hex and prefix "0" if needed
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

const primaryColor = computed(() => {
    const color = props.themeColor;
    if (color.startsWith('hsl')) {
        const [h, s, l] = color.match(/\d+/g).map(Number);
        return hslToHex(h, s, l);
    }
    // remove leading '#' if present
    if (!color.startsWith('#')) {
        return color;
    } else {
        return color.replace(/^#/, '');
    }
});

const widgetLoaded = ref(false);
const widgetContainer = ref(null);

onMounted(() => {
    const initWidget = () => {
        // Wait until our element is truly committed to the document
        // (may not be immediate during out-in page transitions)
        if (!widgetContainer.value || !document.body.contains(widgetContainer.value)) {
            requestAnimationFrame(initWidget);
            return;
        }

        // Remove existing SDK script + iframe + CSS to force re-initialization
        document.getElementById('zenchef-sdk')?.remove();
        document.querySelector('[class^=ZC_sdk__zc-iframe]')?.remove();
        document.querySelectorAll('link[href*="sdk.zenchef.com"]').forEach(l => l.remove());

        const js = document.createElement('script');
        js.id = 'zenchef-sdk';
        js.src = `https://sdk.zenchef.com/v1/sdk.min.js?t=${Date.now()}`;
        document.head.appendChild(js);

        widgetLoaded.value = true;
    };

    nextTick(initWidget);
});

const route = useRoute();
const router = useRouter();
// watch if #zc-action-open is present in the url, if so, remove it from the url
watch(() => route.hash, () => {
    if (route.hash === '#zc-action-open') {
        router.replace({ hash: '' });
    }
}, { immediate: true });

onBeforeUnmount(() => {
    document.getElementById('zenchef-sdk')?.remove();
    document.querySelectorAll('link[href*="sdk.zenchef.com"]').forEach(l => l.remove());

    // XXX: a litle fishy, but it works. Should Zenchef ever change their class names, this will break.
    const iframe = document.querySelector('[class^=ZC_sdk__zc-iframe]');
    if (iframe) {
        iframe.remove();
    }
});
</script>